import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import StarIcon from 'nav/sidepanel/StarIcon';
import {faded} from 'styles/Utilities';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {OptionData} from 'ui-library/menus/MenuConstants';
import OptionContent from 'ui-library/menus/OptionContent';
import {Optional} from 'util/Types';

function isOptionStarrable(option: OptionData, getId: (optionData: OptionData) => string): boolean {
  return Boolean(
    option.value && getId(option) && !option.submenu && !(option.extra && option.extra.unstarrable)
  );
}

interface RenderOptionOptions {
  getId: (optionData: OptionData) => string;
}

/**
 * Returns a function used to render a stars-aware option
 */
export function getRenderStarredOptionContent(
  onToggleStar: (id: string) => void,
  starredIds: Optional<Array<string>>,
  {getId = (optionData: OptionData) => optionData.value.id}: Partial<RenderOptionOptions> = {},
  className?: string,
  dataTestAttrs?: DataTestAttrs
): (optionData: OptionData, shouldFade: boolean) => JSX.Element {
  const starredIdsSet: Optional<Set<string>> = starredIds && new Set(starredIds);
  return (optionData: OptionData, shouldFade: boolean = false): JSX.Element => {
    const {disabled, disabledMessage} = optionData;
    const value =
      disabled && disabledMessage ? `${optionData.name} (${optionData.disabledMessage})` : optionData.name;
    if (!starredIdsSet || !isOptionStarrable(optionData, getId)) {
      return (
        <OptionContent
          className={classNames(className, css(shouldFade && styles.fadedOption))}
          icon={optionData.icon}
          iconClassName={optionData.iconClassName}
          hasSubmenu={Boolean(optionData.submenu)}
          metadata={optionData.metadata}
        >
          {value}
        </OptionContent>
      );
    }
    return (
      <OptionContent
        className={classNames(className, css(shouldFade && styles.fadedOption))}
        icon={optionData.icon}
        iconClassName={optionData.iconClassName}
        hasSubmenu={Boolean(optionData.submenu)}
        dataTestAttrs={dataTestAttrs}
        metadata={optionData.metadata}
      >
        <div className="u-lr-split">
          <span>{value}</span>
          <StarIcon
            onClick={() => {
              onToggleStar(getId(optionData));
            }}
            starred={starredIdsSet.has(getId(optionData))}
          />
        </div>
      </OptionContent>
    );
  };
}
const styles = StyleSheet.create({
  fadedOption: faded,
});
